// [SUPERADMIN-2-BOUTIQUES] - Page Boutiques complète avec tableau et modales
import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { format, formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import {
  Store,
  Search,
  Plus,
  MoreVertical,
  Eye,
  Edit,
  KeyRound,
  Ban,
  Trash2,
  Users,
  AlertTriangle,
  X,
  RefreshCw,
  EyeOff,
  Check,
  Copy,
  ChevronDown,
  ShieldCheck,
  ShieldX
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { COLORS } from "../components/SuperAdminLayout";

// [SUPERADMIN-2-BOUTIQUES] Plans et tarifs
const PLANS = {
  starter: { name: "Starter", price: 29, color: "#3B82F6", bg: "#1E3A5F", includedUsers: 2 },
  pro: { name: "Pro", price: 49, color: "#A78BFA", bg: "#4C1D95", includedUsers: 2, recommended: true },
  business: { name: "Business", price: 89, color: "#FCD34D", bg: "#78350F", includedUsers: 2 }
};

const EXTRA_USER_PRICE = 30;

// [SUPERADMIN-2-BOUTIQUES] Types de boutiques
const BOUTIQUE_TYPES = [
  { value: "pret-a-porter", label: "Prêt-à-porter" },
  { value: "luxe", label: "Luxe & Créateurs" },
  { value: "accessoires", label: "Accessoires" },
  { value: "bijoux", label: "Bijoux" },
  { value: "chaussures", label: "Chaussures" },
  { value: "maroquinerie", label: "Maroquinerie" },
  { value: "vintage", label: "Vintage & Seconde main" },
  { value: "autre", label: "Autre" }
];

// [SUPERADMIN-2-BOUTIQUES] Pays et devises
const COUNTRIES = [
  { code: "FR", name: "France", currency: "EUR", symbol: "€" },
  { code: "BE", name: "Belgique", currency: "EUR", symbol: "€" },
  { code: "CH", name: "Suisse", currency: "CHF", symbol: "CHF" },
  { code: "CA", name: "Canada", currency: "CAD", symbol: "$" },
  { code: "SN", name: "Sénégal", currency: "XOF", symbol: "FCFA" },
  { code: "CI", name: "Côte d'Ivoire", currency: "XOF", symbol: "FCFA" },
  { code: "MA", name: "Maroc", currency: "MAD", symbol: "DH" },
  { code: "TN", name: "Tunisie", currency: "TND", symbol: "DT" }
];

// [SUPERADMIN-2-BOUTIQUES] Données démo enrichies
// [SUPERADMIN-2-BOUTIQUES] Générateur de mot de passe

// Composant Purge
function PurgeButton({ onPurged }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [purging, setPurging] = useState(false);
  const [keepSlug, setKeepSlug] = useState("maison-liora");

  const handlePurge = async () => {
    setPurging(true);
    try {
      const res = await axios.post(`${API}/superadmin/purge-demo-data`, {
        keep_slug: keepSlug,
        confirm: "SUPPRIMER-TOUT"
      });
      const report = res.data;
      toast.success(`Purge terminee ! Boutique gardee : ${report.kept_boutique}`);
      setShowConfirm(false);
      onPurged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la purge");
    } finally {
      setPurging(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setShowConfirm(true)}
        className="gap-2 text-red-500 border-red-200 hover:bg-red-50"
        data-testid="purge-demo-btn"
      >
        <Trash2 className="w-4 h-4" />
        Purger les demos
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: '#EF4444' }}>
              Purger toutes les donnees demo
            </AlertDialogTitle>
            <AlertDialogDescription>
              Cette action va <strong>supprimer toutes les boutiques et leurs donnees</strong> sauf celle que vous gardez.
              Cette action est irreversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div style={{ padding: '0 24px 16px' }}>
            <label style={{ fontSize: 12, fontWeight: 500, color: '#6B7280', display: 'block', marginBottom: 6 }}>
              Slug de la boutique a garder :
            </label>
            <input
              value={keepSlug}
              onChange={(e) => setKeepSlug(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px', borderRadius: 8,
                border: '1px solid #D1D5DB', fontSize: 14
              }}
              data-testid="purge-keep-slug"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handlePurge}
              disabled={purging || !keepSlug}
              style={{ background: '#EF4444' }}
              data-testid="purge-confirm-btn"
            >
              {purging ? "Suppression..." : "Confirmer la purge"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
const generatePassword = () => {
  const words = [
    "Boutique", "Mode", "Style", "Chic", "Luxe", "Perle", "Soie", "Velours",
    "Satin", "Coton", "Bijou", "Eclat", "Dore", "Rose", "Noir", "Blanc"
  ];
  const word1 = words[Math.floor(Math.random() * words.length)];
  const word2 = words[Math.floor(Math.random() * words.length)];
  const num = Math.floor(Math.random() * 9000) + 1000;
  return `${word1}-${word2}-${num}`;
};

// [SUPERADMIN-2-BOUTIQUES] Générateur de slug
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
};

// [SUPERADMIN-2-BOUTIQUES] Générer UUID
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

// [SUPERADMIN-2-BOUTIQUES] Obtenir initiales
const getInitials = (name) => {
  if (!name) return "B";
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// [SUPERADMIN-2-BOUTIQUES] Calculer MRR d'une boutique
const calculateMRR = (boutique) => {
  const plan = PLANS[boutique.plan];
  if (!plan) return 0;
  const extraUsers = Math.max(0, boutique.users - plan.includedUsers);
  return plan.price + (extraUsers * EXTRA_USER_PRICE);
};

// [SUPERADMIN-2-BOUTIQUES] Formater la dernière connexion avec couleur
const formatLastLogin = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  let color = COLORS.positive; // Vert < 7 jours
  if (diffDays >= 7 && diffDays < 30) color = "#FCD34D"; // Orange
  if (diffDays >= 30) color = "#FCA5A5"; // Rouge
  
  const text = formatDistanceToNow(date, { addSuffix: true, locale: fr });
  
  return { text, color };
};

// [SUPERADMIN-2-BOUTIQUES] Configuration des statuts
const STATUS_CONFIG = {
  active: { label: "Actif", bg: "#064E3B", color: "#34D399" },
  inactive: { label: "Inactif", bg: "#1F2937", color: "#6B7280" },
  suspended: { label: "Suspendu", bg: "#450A0A", color: "#FCA5A5" }
};

// [SUPERADMIN-2-BOUTIQUES] Modale création boutique
const CreateBoutiqueModal = ({ isOpen, onClose, onSave, existingSlugs }) => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [country, setCountry] = useState("FR");
  const [type, setType] = useState("");
  const [plan, setPlan] = useState("pro");
  const [ownerFirstName, setOwnerFirstName] = useState("");
  const [ownerLastName, setOwnerLastName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [password, setPassword] = useState(generatePassword());
  const [showPassword, setShowPassword] = useState(true);
  const [slugError, setSlugError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setSlug("");
      setCountry("FR");
      setType("");
      setPlan("pro");
      setOwnerFirstName("");
      setOwnerLastName("");
      setOwnerEmail("");
      setPassword(generatePassword());
      setShowPassword(true);
      setSlugError("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (name) {
      const newSlug = generateSlug(name);
      setSlug(newSlug);
      if (existingSlugs.includes(newSlug)) {
        setSlugError("Ce slug existe déjà");
      } else {
        setSlugError("");
      }
    }
  }, [name, existingSlugs]);

  const handleSlugChange = (value) => {
    const cleanSlug = value.toLowerCase().replace(/[^a-z0-9-]/g, "");
    setSlug(cleanSlug);
    if (existingSlugs.includes(cleanSlug)) {
      setSlugError("Ce slug existe déjà");
    } else {
      setSlugError("");
    }
  };

  const selectedCountry = COUNTRIES.find(c => c.code === country);

  const handleSubmit = async () => {
    if (!name || !slug || !type || !ownerFirstName || !ownerLastName || !ownerEmail) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    if (slugError) {
      toast.error("Le slug est déjà utilisé");
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 500));

    const newBoutique = {
      id: generateUUID(),
      name,
      slug,
      plan,
      status: "active",
      users: 1,
      email: ownerEmail,
      ownerName: `${ownerFirstName} ${ownerLastName}`,
      country,
      currency: selectedCountry?.currency || "EUR",
      type,
      password: btoa(password),
      compteValide: false,
      createdAt: new Date().toISOString(),
      lastLogin: null
    };

    onSave(newBoutique);
    setIsSubmitting(false);
    onClose();
    toast.success(`Boutique ${name} créée !`);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="w-full sm:max-w-[540px] rounded-t-[20px] sm:rounded-[20px] p-7 max-h-[90vh] overflow-y-auto"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-bold text-lg" style={{ color: COLORS.text }}>Créer une boutique</h2>
            <button onClick={onClose} style={{ color: COLORS.textSecondary }}>
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Nom */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Nom de la boutique *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ma Boutique"
                className="w-full rounded-xl px-4 py-3 placeholder:text-gray-400 focus:outline-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Slug (URL)</label>
              <input
                type="text"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                className="w-full rounded-xl px-4 py-3 focus:outline-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${slugError ? '#EF4444' : COLORS.border}`, color: COLORS.text }}
              />
              {slugError && <p className="text-red-500 text-xs mt-1">{slugError}</p>}
            </div>

            {/* Pays */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Pays & Devise</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-xl px-4 py-3 focus:outline-none appearance-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              >
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.name} ({c.currency})</option>
                ))}
              </select>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Type de boutique *</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                className="w-full rounded-xl px-4 py-3 focus:outline-none appearance-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              >
                <option value="">Sélectionner...</option>
                {BOUTIQUE_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            {/* Plan */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Plan *</label>
              <div className="space-y-2">
                {Object.entries(PLANS).map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPlan(key)}
                    className="w-full p-4 rounded-xl text-left transition-all"
                    style={{
                      backgroundColor: plan === key ? `${COLORS.accent}10` : '#F8FAFC',
                      border: `2px solid ${plan === key ? COLORS.accent : COLORS.border}`
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-semibold" style={{ color: COLORS.text }}>{p.name.toUpperCase()}</span>
                        {p.recommended && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: COLORS.accent, color: 'white' }}>
                            Recommandé
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold" style={{ color: COLORS.text }}>{p.price}€</span>
                        <span style={{ color: COLORS.textSecondary }}>/mois</span>
                        {plan === key && <Check className="w-5 h-5" style={{ color: COLORS.accent }} />}
                      </div>
                    </div>
                    <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                      {p.includedUsers} utilisateurs inclus · +{EXTRA_USER_PRICE}€ par utilisateur supplémentaire
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Propriétaire */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Prénom propriétaire *</label>
                <input
                  type="text"
                  value={ownerFirstName}
                  onChange={(e) => setOwnerFirstName(e.target.value)}
                  placeholder="Marie"
                  className="w-full rounded-xl px-4 py-3 placeholder:text-gray-400 focus:outline-none"
                  style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
                />
              </div>
              <div>
                <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Nom *</label>
                <input
                  type="text"
                  value={ownerLastName}
                  onChange={(e) => setOwnerLastName(e.target.value)}
                  placeholder="Dupont"
                  className="w-full rounded-xl px-4 py-3 placeholder:text-gray-400 focus:outline-none"
                  style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Email propriétaire *</label>
              <input
                type="email"
                value={ownerEmail}
                onChange={(e) => setOwnerEmail(e.target.value)}
                placeholder="marie@boutique.com"
                className="w-full rounded-xl px-4 py-3 placeholder:text-gray-400 focus:outline-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Mot de passe *</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  readOnly
                  className="w-full rounded-xl px-4 py-3 pr-24 font-mono focus:outline-none"
                  style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPassword(generatePassword())}
                    className="p-2 hover:bg-slate-200 rounded transition-colors"
                    style={{ color: COLORS.textSecondary }}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="p-2 hover:bg-slate-200 rounded transition-colors"
                    style={{ color: COLORS.textSecondary }}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                Ce mot de passe sera envoyé au propriétaire de la boutique.
              </p>
            </div>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="w-full py-4 rounded-xl font-semibold mt-4"
              style={{ backgroundColor: COLORS.accent }}
            >
              {isSubmitting ? "Création..." : "Créer la boutique"}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [SUPERADMIN-2-BOUTIQUES] Modale reset password
const ResetPasswordModal = ({ isOpen, onClose, boutique, onSave }) => {
  const [password, setPassword] = useState(generatePassword());
  const [showPassword, setShowPassword] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setPassword(generatePassword());
      setShowPassword(true);
      setCopied(false);
    }
  }, [isOpen]);

  const handleCopy = () => {
    navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = () => {
    onSave(boutique.id, password);
    onClose();
    toast.success("Mot de passe mis à jour");
  };

  if (!isOpen || !boutique) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-md rounded-2xl p-6"
          style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
          onClick={e => e.stopPropagation()}
        >
          <h2 className="font-bold text-lg mb-4" style={{ color: COLORS.text }}>Réinitialiser le mot de passe</h2>
          
          <div className="mb-4">
            <p className="font-medium" style={{ color: COLORS.text }}>{boutique.name}</p>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>{boutique.email}</p>
          </div>

          <div>
            <label className="block text-sm mb-2" style={{ color: COLORS.textSecondary }}>Nouveau mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                readOnly
                className="w-full rounded-xl px-4 py-3 pr-28 font-mono focus:outline-none"
                style={{ backgroundColor: '#F8FAFC', border: `1px solid ${COLORS.border}`, color: COLORS.text }}
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                <button onClick={() => setPassword(generatePassword())} className="p-2 hover:bg-slate-200 rounded" style={{ color: COLORS.textSecondary }}>
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button onClick={() => setShowPassword(!showPassword)} className="p-2 hover:bg-slate-200 rounded" style={{ color: COLORS.textSecondary }}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
                <button onClick={handleCopy} className="p-2 hover:bg-slate-200 rounded" style={{ color: COLORS.textSecondary }}>
                  {copied ? <Check className="w-4 h-4" style={{ color: COLORS.positive }} /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button variant="outline" onClick={onClose} className="flex-1" style={{ borderColor: COLORS.border, color: COLORS.text }}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} className="flex-1" style={{ backgroundColor: COLORS.accent }}>
              Mettre à jour
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// [SUPERADMIN-2-BOUTIQUES] Composant principal
export default function SuperAdminBoutiques() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(searchParams.get("filter") || "all");
  const [boutiques, setBoutiques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [resetPasswordBoutique, setResetPasswordBoutique] = useState(null);
  const [deletingBoutique, setDeletingBoutique] = useState(null);
  const [validatingBoutique, setValidatingBoutique] = useState(null);
  const [revokingBoutique, setRevokingBoutique] = useState(null);

  // [REAL-DATA] Fetch boutiques from MongoDB
  useEffect(() => {
    const fetchBoutiques = async () => {
      try {
        const res = await axios.get(`${API}/admin/boutiques`);
        const mapped = res.data.map(b => ({
          id: b.id || b.admin_id,
          name: b.boutique_name || b.name || b.slug,
          slug: b.slug,
          plan: b.plan || "starter",
          status: b.status || "active",
          users: b.users || 1,
          maxUsers: b.max_users || 2,
          email: b.owner_email || "",
          ownerName: b.owner_name || b.boutique_name || "",
          country: b.country_code || "FR",
          currency: b.currency || "EUR",
          type: b.boutique_type || "autre",
          compteValide: b.compteValide === true,
          codeAcces: b.code_acces || "",
          createdAt: b.created_at || new Date().toISOString(),
          lastLogin: b.last_login || null
        }));
        setBoutiques(mapped);
      } catch (err) {
        console.error("Erreur chargement boutiques:", err);
        toast.error("Impossible de charger les boutiques");
      } finally {
        setLoading(false);
      }
    };
    fetchBoutiques();
  }, []);

  // Filtrer les boutiques
  const filteredBoutiques = useMemo(() => {
    let result = [...boutiques];
    
    if (filterStatus === "validated") {
      result = result.filter(b => b.compteValide === true);
    } else if (filterStatus === "pending_validation") {
      result = result.filter(b => b.compteValide === false);
    } else if (filterStatus === "suspended") {
      result = result.filter(b => b.status === "suspended");
    }
    
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(b => 
        b.name.toLowerCase().includes(searchLower) ||
        b.slug.toLowerCase().includes(searchLower) ||
        b.email.toLowerCase().includes(searchLower)
      );
    }
    
    return result;
  }, [boutiques, filterStatus, search]);

  const existingSlugs = useMemo(() => boutiques.map(b => b.slug), [boutiques]);

  const handleCreateBoutique = (newBoutique) => {
    setBoutiques([newBoutique, ...boutiques]);
  };

  const handleResetPassword = (boutiqueId, newPassword) => {
    setBoutiques(boutiques.map(b => 
      b.id === boutiqueId ? { ...b, password: btoa(newPassword) } : b
    ));
  };

  const handleDelete = () => {
    if (!deletingBoutique) return;
    setBoutiques(boutiques.filter(b => b.id !== deletingBoutique.id));
    setDeletingBoutique(null);
    toast.success("Boutique supprimée");
  };

  const handleSuspend = (boutique) => {
    const newStatus = boutique.status === "suspended" ? "active" : "suspended";
    setBoutiques(boutiques.map(b => 
      b.id === boutique.id ? { ...b, status: newStatus } : b
    ));
    toast.success(newStatus === "suspended" ? "Boutique suspendue" : "Boutique réactivée");
  };

  const handleToggleValidation = (boutique) => {
    if (boutique.compteValide) {
      setRevokingBoutique(boutique);
    } else {
      setValidatingBoutique(boutique);
    }
  };

  const confirmValidation = async () => {
    if (!validatingBoutique) return;
    try {
      // [VALIDATION-FIX] Appel API pour valider dans MongoDB
      await axios.put(`${API}/admin/boutiques/${validatingBoutique.slug}/valider`, {
        compteValide: true
      });
      setBoutiques(boutiques.map(b =>
        b.id === validatingBoutique.id ? { ...b, compteValide: true } : b
      ));
      toast.success(`Compte "${validatingBoutique.name}" validé ! Accès complet activé.`, {
        style: { backgroundColor: '#064E3B', color: '#34D399', border: 'none' }
      });
    } catch (error) {
      // Si la boutique n'est pas dans MongoDB (demo data), update local seulement
      setBoutiques(boutiques.map(b =>
        b.id === validatingBoutique.id ? { ...b, compteValide: true } : b
      ));
      toast.success(`Compte "${validatingBoutique.name}" validé ! Accès complet activé.`, {
        style: { backgroundColor: '#064E3B', color: '#34D399', border: 'none' }
      });
    }
    setValidatingBoutique(null);
  };

  const confirmRevoke = async () => {
    if (!revokingBoutique) return;
    try {
      // [VALIDATION-FIX] Appel API pour révoquer dans MongoDB
      await axios.put(`${API}/admin/boutiques/${revokingBoutique.slug}/valider`, {
        compteValide: false
      });
      setBoutiques(boutiques.map(b =>
        b.id === revokingBoutique.id ? { ...b, compteValide: false } : b
      ));
      toast.warning(`Accès restreint à la caisse pour "${revokingBoutique.name}".`, {
        style: { backgroundColor: '#78350F', color: '#FCD34D', border: 'none' }
      });
    } catch (error) {
      setBoutiques(boutiques.map(b =>
        b.id === revokingBoutique.id ? { ...b, compteValide: false } : b
      ));
      toast.warning(`Accès restreint à la caisse pour "${revokingBoutique.name}".`, {
        style: { backgroundColor: '#78350F', color: '#FCD34D', border: 'none' }
      });
    }
    setRevokingBoutique(null);
  };

  return (
    <div className="space-y-6" data-testid="superadmin-boutiques">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: COLORS.text }}>Boutiques</h1>
          <p className="text-sm" style={{ color: COLORS.textSecondary }}>
            {boutiques.length} boutique{boutiques.length > 1 ? 's' : ''} au total
          </p>
        </div>
        <div className="flex gap-2">
          {boutiques.length > 1 && (
            <PurgeButton onPurged={() => fetchBoutiques()} />
          )}
          <Button
            onClick={() => setShowCreateModal(true)}
            className="gap-2"
            style={{ backgroundColor: COLORS.accent }}
          >
            <Plus className="w-4 h-4" />
            Creer une boutique
          </Button>
        </div>
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Recherche */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: COLORS.textSecondary }} />
          <input
            type="text"
            placeholder="Rechercher une boutique..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl placeholder:text-gray-400 focus:outline-none shadow-sm"
            style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}`, color: COLORS.text }}
          />
        </div>

        {/* Filtres */}
        <div className="flex rounded-xl overflow-hidden flex-shrink-0 shadow-sm flex-wrap" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          {[
            { key: "all", label: "Toutes" },
            { key: "validated", label: "Validées" },
            { key: "pending_validation", label: "En attente" },
            { key: "suspended", label: "Suspendues" },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilterStatus(f.key)}
              className="px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap"
              style={{
                backgroundColor: filterStatus === f.key ? COLORS.accent : 'transparent',
                color: filterStatus === f.key ? 'white' : COLORS.textSecondary
              }}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tableau */}
      <Card className="overflow-hidden shadow-sm" style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
        {/* Header tableau */}
        <div 
          className="hidden lg:grid grid-cols-[2fr,1fr,1fr,1fr,1fr,1fr,80px,80px,60px] gap-4 px-6 py-3 text-xs uppercase font-medium"
          style={{ backgroundColor: '#F1F5F9', color: COLORS.textSecondary }}
        >
          <span>Boutique</span>
          <span>Plan</span>
          <span>Utilisateurs</span>
          <span>MRR</span>
          <span>Statut</span>
          <span>Validation</span>
          <span>Connexion</span>
          <span>Code</span>
          <span></span>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <RefreshCw className="w-6 h-6 animate-spin" style={{ color: COLORS.textSecondary }} />
            <span className="ml-3 text-sm" style={{ color: COLORS.textSecondary }}>Chargement des boutiques...</span>
          </div>
        )}

        {/* Lignes */}
        {!loading && <div className="divide-y" style={{ borderColor: COLORS.border }}>
          {filteredBoutiques.map((boutique, index) => {
            const plan = PLANS[boutique.plan];
            const mrr = calculateMRR(boutique);
            const extraUsers = Math.max(0, boutique.users - (plan?.includedUsers || 2));
            const status = STATUS_CONFIG[boutique.status] || STATUS_CONFIG.inactive;
            const lastLogin = boutique.lastLogin ? formatLastLogin(boutique.lastLogin) : { text: "Jamais", color: COLORS.textSecondary };

            return (
              <motion.div
                key={boutique.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: index * 0.03 }}
                className="lg:grid lg:grid-cols-[2fr,1fr,1fr,1fr,1fr,1fr,80px,80px,60px] gap-4 p-4 lg:px-6 lg:py-4 items-center hover:bg-slate-50 transition-colors"
              >
                {/* Boutique */}
                <div className="flex items-center gap-3 mb-3 lg:mb-0">
                  <div 
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ backgroundColor: plan?.color || COLORS.accent }}
                  >
                    {getInitials(boutique.name)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: COLORS.text }}>{boutique.name}</p>
                    <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>/{boutique.slug}</p>
                    <p className="text-xs truncate lg:hidden" style={{ color: COLORS.textSecondary }}>{boutique.email}</p>
                  </div>
                </div>

                {/* Plan */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Plan:</span>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${plan?.color}15`, color: plan?.color }}
                  >
                    {plan?.name}
                  </span>
                </div>

                {/* Utilisateurs */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Utilisateurs:</span>
                  <div className="flex items-center gap-1">
                    <Users className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                    <span className="text-sm" style={{ color: COLORS.text }}>
                      max:
                    </span>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      defaultValue={boutique.maxUsers}
                      className="w-12 text-center text-sm font-bold rounded px-1 py-0.5"
                      style={{
                        background: COLORS.cardBg,
                        color: COLORS.text,
                        border: `1px solid ${COLORS.border}`,
                      }}
                      onBlur={async (e) => {
                        const val = parseInt(e.target.value);
                        if (val && val !== boutique.maxUsers) {
                          try {
                            const slug = boutique.slug || boutique.id;
                            await axios.put(`${API}/superadmin/boutiques/${slug}/max-users`, { max_users: val });
                            boutique.maxUsers = val;
                            toast.success(`Limite mise à jour : ${val} utilisateurs`);
                          } catch { toast.error("Erreur"); }
                        }
                      }}
                      data-testid={`max-users-${boutique.slug}`}
                    />
                  </div>
                </div>

                {/* MRR */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>MRR:</span>
                  <span className="font-semibold" style={{ color: COLORS.text }}>{mrr}€</span>
                </div>

                {/* Statut */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Statut:</span>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${status.color}15`, color: status.color }}
                  >
                    {status.label}
                  </span>
                </div>

                {/* Validation */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Validation:</span>
                  {boutique.compteValide ? (
                    <span 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: '#064E3B', color: '#34D399' }}
                      data-testid={`validation-badge-${boutique.id}`}
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                      Validé
                    </span>
                  ) : (
                    <span 
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium validation-pulse"
                      style={{ backgroundColor: '#78350F', color: '#FCD34D' }}
                      data-testid={`validation-badge-${boutique.id}`}
                    >
                      <ShieldX className="w-3.5 h-3.5" />
                      En attente
                    </span>
                  )}
                </div>

                {/* Dernière connexion */}
                <div className="flex items-center gap-2 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Connexion:</span>
                  <span className="text-sm" style={{ color: lastLogin.color }}>
                    {lastLogin.text}
                  </span>
                </div>

                {/* [CODE-CLIENTE] Code accès */}
                <div className="flex items-center gap-1 mb-2 lg:mb-0">
                  <span className="lg:hidden text-xs" style={{ color: COLORS.textSecondary }}>Code:</span>
                  {boutique.codeAcces ? (
                    <span 
                      className="px-2.5 py-1 rounded-md text-xs font-bold cursor-pointer"
                      style={{ 
                        background: '#1A1A1A', 
                        border: '1px solid #2A2A2A', 
                        fontFamily: 'monospace', 
                        letterSpacing: '2px',
                        color: COLORS.text
                      }}
                      onClick={() => { navigator.clipboard.writeText(boutique.codeAcces); toast.success("Code copié !"); }}
                      title="Cliquer pour copier"
                    >
                      {boutique.codeAcces}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: COLORS.textSecondary }}>—</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" style={{ color: COLORS.textSecondary }} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent 
                      align="end" 
                      className="w-52"
                      style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
                    >
                      <DropdownMenuItem style={{ color: COLORS.text }}>
                        <Eye className="w-4 h-4 mr-2" />
                        Voir le détail
                      </DropdownMenuItem>
                      <DropdownMenuItem style={{ color: COLORS.text }}>
                        <Edit className="w-4 h-4 mr-2" />
                        Modifier le plan
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        style={{ color: COLORS.text }}
                        onClick={() => setResetPasswordBoutique(boutique)}
                      >
                        <KeyRound className="w-4 h-4 mr-2" />
                        Réinitialiser mot de passe
                      </DropdownMenuItem>
                      <DropdownMenuSeparator style={{ backgroundColor: COLORS.border }} />
                      <DropdownMenuItem 
                        style={{ color: boutique.compteValide ? COLORS.warning : COLORS.positive }}
                        onClick={() => handleToggleValidation(boutique)}
                        data-testid={`toggle-validation-${boutique.id}`}
                      >
                        {boutique.compteValide ? (
                          <><ShieldX className="w-4 h-4 mr-2" />Révoquer l'accès</>
                        ) : (
                          <><ShieldCheck className="w-4 h-4 mr-2" />Valider le compte</>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        style={{ color: COLORS.warning }}
                        onClick={() => handleSuspend(boutique)}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        {boutique.status === "suspended" ? "Réactiver" : "Suspendre la boutique"}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        style={{ color: COLORS.negative }}
                        onClick={() => setDeletingBoutique(boutique)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </motion.div>
            );
          })}
        </div>}

        {/* Empty state */}
        {!loading && filteredBoutiques.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16">
            <Store className="w-12 h-12 mb-4" style={{ color: COLORS.textSecondary }} />
            <p className="font-medium mb-1" style={{ color: COLORS.text }}>Aucune boutique trouvée</p>
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>
              Modifiez vos critères de recherche ou créez une nouvelle boutique
            </p>
          </div>
        )}
      </Card>

      {/* Modale création */}
      <CreateBoutiqueModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleCreateBoutique}
        existingSlugs={existingSlugs}
      />

      {/* Modale reset password */}
      <ResetPasswordModal
        isOpen={!!resetPasswordBoutique}
        onClose={() => setResetPasswordBoutique(null)}
        boutique={resetPasswordBoutique}
        onSave={handleResetPassword}
      />

      {/* Dialog suppression */}
      <AlertDialog open={!!deletingBoutique} onOpenChange={() => setDeletingBoutique(null)}>
        <AlertDialogContent style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: COLORS.text }}>Supprimer cette boutique ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: COLORS.textSecondary }}>
              Cette action est irréversible. Toutes les données de <span className="font-medium" style={{ color: COLORS.text }}>{deletingBoutique?.name}</span> seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ backgroundColor: '#F1F5F9', color: COLORS.text, border: 'none' }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              style={{ backgroundColor: COLORS.negative }}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog validation compte */}
      <AlertDialog open={!!validatingBoutique} onOpenChange={() => setValidatingBoutique(null)}>
        <AlertDialogContent style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: COLORS.text }}>
              Valider la boutique {validatingBoutique?.name} ?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: COLORS.textSecondary }}>
              La gérante aura accès à tous les modules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ backgroundColor: '#F1F5F9', color: COLORS.text, border: 'none' }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmValidation}
              style={{ backgroundColor: COLORS.positive }}
              data-testid="confirm-validate-btn"
            >
              Valider
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog révoquer accès */}
      <AlertDialog open={!!revokingBoutique} onOpenChange={() => setRevokingBoutique(null)}>
        <AlertDialogContent style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: COLORS.text }}>
              Révoquer l'accès de {revokingBoutique?.name} ?
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: COLORS.textSecondary }}>
              L'accès sera restreint à la caisse uniquement.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ backgroundColor: '#F1F5F9', color: COLORS.text, border: 'none' }}>
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRevoke}
              style={{ backgroundColor: COLORS.warning }}
              data-testid="confirm-revoke-btn"
            >
              Révoquer l'accès
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pulse animation for pending badges */}
      <style>{`
        .validation-pulse {
          animation: validationPulse 2s ease-in-out infinite;
        }
        @keyframes validationPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
}
